import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Info, ThumbsDownIcon, ThumbsUpIcon } from '@phosphor-icons/react';
import {
	Check,
	ChevronDown,
	ChevronRight,
	History,
	Pencil,
	Undo2,
	X,
} from 'lucide-react';

import { useSubmitFeedbackMutation, useUpdateLineItemMutation } from '../api';
import { formatCurrency, formatPreciseFee } from '../utils/utils';

import { DraggableLineItem } from './analysis/DraggableLineItem';
import { DroppableSection } from './analysis/DroppableSection';
import { GapDropZone } from './analysis/GapDropZone';
import {
	FLAGGED_ROW_CLASS,
	LineReviewBadge,
	useFlaggedLines,
} from './analysis/LineReview';
import { FEE_CUR_BG, FEE_NEW_BG, WIN_TEXT } from './analysis/v9';
import FeedbackModal from './FeedbackModal';
import LineItemAuditLogModal from './LineItemAuditLogModal';
import Loader from './Loader';

import type {
	FeeItemValidation,
	LineItemEditState,
	UnifiedFeeItem,
	UnifiedOtherService,
} from '../types';

// Helper function to get background color for rows (simplified - no anomaly highlighting)
// Helper to check if parent has modified children
// CRITICAL: Only check modifications for the processor side we're viewing
const isParentModified = (
	item: UnifiedFeeItem | UnifiedOtherService,
	allItems: (UnifiedFeeItem | UnifiedOtherService)[],
	isCurrentProcessor: boolean,
): boolean => {
	if (!item.children || item.children.length === 0) return false;

	// Check if any child has modifications for the relevant processor side
	return item.children.some((child) => {
		const childExtra = child.extra_attributes || {};
		// Check if child has move_history (moves affect both sides, so always check)
		if (
			childExtra.move_history &&
			Array.isArray(childExtra.move_history) &&
			childExtra.move_history.length > 0
		) {
			return true;
		}
		// Check if child has user_edits for the relevant processor side only
		const userEdits = childExtra.user_edits || {};
		if (isCurrentProcessor) {
			// Left side - only check ic_data edits
			if (userEdits.ic_data && Object.keys(userEdits.ic_data).length > 0) {
				return true;
			}
		} else {
			// Right side (IC+) - only check ic_plus_data edits
			if (
				userEdits.ic_plus_data &&
				Object.keys(userEdits.ic_plus_data).length > 0
			) {
				return true;
			}
		}
		// Check if child was moved TO this parent (original_parent_id differs)
		// Moves affect both sides, so always check
		if (
			childExtra.original_parent_id &&
			childExtra.parent_id &&
			childExtra.original_parent_id !== childExtra.parent_id
		) {
			return true;
		}
		return false;
	});
};

const getRowBackgroundColor = (
	isHovered?: boolean,
	isMoved?: boolean,
	item?: UnifiedFeeItem | UnifiedOtherService,
	allItems?: (UnifiedFeeItem | UnifiedOtherService)[],
	isCurrentProcessor?: boolean,
): string => {
	// Check if parent has modified children (for row highlighting)
	// If isCurrentProcessor is provided, only check that side; otherwise check both sides
	if (item && allItems) {
		if (isCurrentProcessor !== undefined) {
			// Inside ColumnComponent - check only the relevant processor side
			if (isParentModified(item, allItems, isCurrentProcessor)) {
				return 'bg-amber-50'; // Yellow highlight same as edited items
			}
		} else {
			// Outside ColumnComponent (shared row) - check both sides
			// Highlight if either side has modifications
			if (
				isParentModified(item, allItems, true) ||
				isParentModified(item, allItems, false)
			) {
				return 'bg-amber-50'; // Yellow highlight same as edited items
			}
		}
	}
	if (isMoved) return 'bg-amber-50/60';
	return isHovered
		? 'bg-indigo-50/40'
		: 'bg-gradient-to-r from-gray-50/80 to-transparent';
};

// Helper function to get cell-level anomaly highlighting for padded rates
const getAnomalyCellStyle = (
	validation?: FeeItemValidation,
): { backgroundColor: string; tooltip?: string; textColorClass?: string } => {
	if (!validation || !validation.has_anomaly || !validation.severity) {
		return { backgroundColor: '' };
	}

	const severityKey = validation.severity.toLowerCase();
	const colors: Record<string, string> = {
		low: 'bg-sand-100/80',
		medium: 'bg-sand-200/80',
		high: 'bg-red-200/80',
		critical: 'bg-red-300/80',
	};

	const tooltipLines: string[] = [];
	if (validation.explanation) {
		tooltipLines.push(validation.explanation);
	}

	const {
		overcharge_amount: overchargeAmountRaw,
		deviation_percentage: deviationRaw,
	} = validation;
	const isUndercharge =
		typeof overchargeAmountRaw === 'number' && overchargeAmountRaw < 0;
	const chargeLabel = isUndercharge ? 'Undercharge' : 'Overcharge';

	let chargeText: string | undefined;
	if (
		typeof overchargeAmountRaw === 'number' &&
		typeof deviationRaw === 'number'
	) {
		const formattedAmount = formatCurrency(Math.abs(overchargeAmountRaw));
		const formattedDeviation = `${Math.abs(deviationRaw * 100).toFixed(1)}%`;
		chargeText = `${chargeLabel}: ${formattedAmount} (${formattedDeviation})`;
	}

	if (chargeText) {
		const hasExistingCharge = tooltipLines.some((line) =>
			line.toLowerCase().includes(chargeLabel.toLowerCase()),
		);
		if (!hasExistingCharge) {
			tooltipLines.push(chargeText);
		}
	}

	return {
		backgroundColor: isUndercharge ? '' : colors[severityKey] || '',
		tooltip: tooltipLines.join(' • '),
		textColorClass: isUndercharge ? 'text-green-600' : undefined,
	};
};

// Use the new DraggableLineItem component (aliased for backward compatibility)
const DraggableRow: React.FC<{
	id: string;
	children: React.ReactNode;
	disabled?: boolean;
	item?: UnifiedFeeItem | UnifiedOtherService;
	categoryId?: string;
	isParent?: boolean; // Whether this row can accept children (has children or could have children)
	draggedItemHasChildren?: boolean; // Whether the currently dragged item has children
	isDraggingActive?: boolean; // Whether any item is currently being dragged
	isRecentlyDropped?: boolean; // Whether this item was recently dropped (for 10-second highlight)
	canUndo?: boolean; // Whether this item can be undone
	onUndo?: (item?: UnifiedFeeItem | UnifiedOtherService) => void; // Callback to undo the drop (item optional for persistent undo)
	isUndoing?: boolean; // Whether an undo operation is in progress
}> = (props) => {
	// Delegate to the new DraggableLineItem component
	return <DraggableLineItem {...props} />;
};

// Use the new DroppableSection component (aliased for backward compatibility)
const DroppableCategory: React.FC<{
	id: string;
	children: React.ReactNode;
	isDragging?: boolean;
	isSource?: boolean;
	draggedItemHasChildren?: boolean; // Whether the dragged item has children (affects which drop zones are valid)
}> = (props) => {
	// Delegate to the new DroppableSection component
	return <DroppableSection {...props} />;
};

// Helper to determine if this processor has the winning (lower) fee for a given item
const isWinningFee = (
	item: UnifiedFeeItem | UnifiedOtherService,
	isCurrentProcessor: boolean,
): boolean => {
	const isOtherService = !('rate' in (item as any).ic);
	const icFee = isOtherService
		? (item as UnifiedOtherService).ic.feeDue
		: (item as UnifiedFeeItem).ic.feeDue;
	const icPlusFee = isOtherService
		? (item as UnifiedOtherService).icPlus.feeDue
		: (item as UnifiedFeeItem).icPlus.feeDue;

	// Handle edge cases
	if (icFee === icPlusFee) return false; // Tie - no winner
	if (isNaN(icFee) || icFee === null || icFee === undefined)
		return !isCurrentProcessor;
	if (isNaN(icPlusFee) || icPlusFee === null || icPlusFee === undefined)
		return isCurrentProcessor;

	// Return true if this processor has the lower fee
	return isCurrentProcessor ? icFee < icPlusFee : icPlusFee < icFee;
};

// Helper to check if a field has been edited by the user
// IMPORTANT: Parent items (parent_id is null) should never show as edited
// Their amounts are system-calculated from children, not user-edited
const isFieldUserEdited = (
	item: UnifiedFeeItem | UnifiedOtherService,
	columnKey: string,
	isCurrentProcessor: boolean,
): boolean => {
	// Parent items (parent_id is null) should never show as edited
	// Their amounts are system-calculated, not user-edited
	const parentId = item.parent_id;
	if (parentId === null || parentId === undefined) {
		return false;
	}

	const userEdits = item.extra_attributes?.user_edits;
	if (!userEdits) return false;

	// Map column keys to backend field names
	let fieldName = columnKey;
	if (columnKey === 'fee') fieldName = 'per_trans';
	if (columnKey === 'count') fieldName = 'count';
	if (columnKey === 'feeDue') fieldName = 'amount';

	// CRITICAL FIX: Only check edits for the processor side we're currently viewing
	// - Left side (isCurrentProcessor=true): only check ic_data edits
	// - Right side (isCurrentProcessor=false/IC+): only check ic_plus_data edits
	// This prevents highlighting the wrong side when editing IC+ fields
	if (isCurrentProcessor) {
		// Left side - only check ic_data edits
		return userEdits.ic_data?.[fieldName] !== undefined;
	} else {
		// Right side (IC+) - only check ic_plus_data edits
		return userEdits.ic_plus_data?.[fieldName] !== undefined;
	}
};

// Helper to get original value for a field
const getOriginalValue = (
	item: UnifiedFeeItem | UnifiedOtherService,
	columnKey: string,
	isCurrentProcessor: boolean,
): any => {
	const originalValues = item.extra_attributes?.original_values;
	if (!originalValues) return null;

	const processorData = isCurrentProcessor
		? originalValues.ic_data
		: originalValues.ic_plus_data;
	if (!processorData) return null;

	// Map column keys to backend field names
	let fieldName = columnKey;
	if (columnKey === 'fee') fieldName = 'per_trans';
	if (columnKey === 'count') fieldName = 'count';
	if (columnKey === 'feeDue') fieldName = 'amount';

	return processorData[fieldName];
};

// Helper to format original value for display
const formatOriginalValue = (value: any, columnKey: string): string => {
	if (value === null || value === undefined) return 'N/A';
	if (columnKey === 'rate') {
		return typeof value === 'number' ? `${(value * 100).toFixed(3)}%` : value;
	}
	if (columnKey === 'fee') {
		// Per-item fee — show at source precision (e.g. $0.0195), not rounded.
		return typeof value === 'number' ? formatPreciseFee(value) : value;
	}
	if (columnKey === 'feeDue') {
		return typeof value === 'number' ? formatCurrency(value) : value;
	}
	if (columnKey === 'volume') {
		return typeof value === 'number' ? formatCurrency(value) : value;
	}
	return String(value);
};

// ColumnComponent - removed React.memo to allow proper re-renders after data updates
const ColumnComponent = ({
	columns,
	data,
	isCurrentProcessor,
	showLoader,
	total,
	processorName,
	processorNamePlus,
	expandedRows,
	hoveredRow,
	onHoverChange,
	showChildren = true,
	enableEditing = false,
	editingCellId = null,
	lineItemEdits = {},
	onEditChange,
	getCurrentValue,
	onStartEdit,
	onSaveEdit,
	onCancelEdit,
	savingLineItemId = null,
	sortConfig,
	onSort,
	hasFieldChanged,
	showRateDisclaimer = false,
}: {
	columns: Array<{ key: string; label: string; width: string; align: string }>;
	data: (UnifiedFeeItem | UnifiedOtherService)[];
	isCurrentProcessor: boolean;
	showLoader: boolean;
	total: number;
	processorName?: string;
	processorNamePlus?: string;
	expandedRows: Set<string>;
	hoveredRow: string | null;
	onHoverChange: (lineItemId: string | null) => void;
	showChildren?: boolean;
	enableEditing?: boolean;
	editingCellId?: string | null;
	lineItemEdits?: Record<string, any>;
	onEditChange?: (
		lineItemId: string,
		field: keyof LineItemEditState,
		value: string | number,
	) => void;
	getCurrentValue?: (
		lineItemId: string,
		field: keyof LineItemEditState,
		originalValue: any,
		isCurrentProcessor?: boolean,
	) => any;
	onStartEdit?: (
		lineItemId: string,
		item: UnifiedFeeItem | UnifiedOtherService,
		isCurrentProcessor: boolean,
		columnKey: string,
	) => void;
	onSaveEdit?: () => void;
	onCancelEdit?: () => void;
	savingLineItemId?: string | null;
	sortConfig: {
		column: string | null;
		direction: 'asc' | 'desc';
		isCurrentProcessor?: boolean;
	};
	onSort: (columnKey: string, isCurrentProcessor: boolean) => void;
	hasFieldChanged?: (
		lineItemId: string,
		field: 'rate' | 'fee' | 'volume' | 'count' | 'feeDue',
		originalItem: UnifiedFeeItem | UnifiedOtherService,
		isCurrentProcessor: boolean,
	) => boolean;
	showRateDisclaimer?: boolean;
}) => {
	const getCellValue = useCallback(
		(
			item: UnifiedFeeItem | UnifiedOtherService,
			columnKey: string,
			isCurrentProcessor: boolean,
		) => {
			const isOtherService = !('rate' in (item as any).ic);

			// Get the appropriate data source based on processor type
			const dataSource = isCurrentProcessor
				? isOtherService
					? (item as UnifiedOtherService).ic
					: (item as UnifiedFeeItem).ic
				: isOtherService
					? (item as UnifiedOtherService).icPlus
					: (item as UnifiedFeeItem).icPlus;

			switch (columnKey) {
				case 'count':
					return dataSource.count;
				case 'rate':
					return isOtherService ? '-' : (dataSource as any).rate;
				case 'fee':
					// Per-item fee — full source precision (e.g. $0.0195), not rounded.
					return formatPreciseFee(
						(dataSource as any).perTrans ?? (dataSource as any).fee ?? 0,
					);
				case 'volume':
					return formatCurrency((dataSource as any).volume || 0);
				case 'feeDue':
					return formatCurrency(dataSource.feeDue);
				default:
					return '-';
			}
		},
		[],
	);

	return (
		<div
			className={`flex-1 ${isCurrentProcessor ? FEE_CUR_BG : FEE_NEW_BG} inline-flex flex-col justify-center items-start`}
			role="column"
			aria-label={isCurrentProcessor ? processorName : processorNamePlus}
		>
			<div
				className="w-full h-10 px-3 bg-black/5 border-b border-black/5 flex flex-col justify-center items-center"
				role="columnheader"
			>
				<div className="text-center text-sm font-medium leading-none">
					{isCurrentProcessor ? processorName : processorNamePlus}
				</div>
			</div>
			<div
				className="w-full inline-flex justify-start items-start"
				role="rowgroup"
			>
				{columns.map((column, columnIndex) => {
					const isFirstColumn = columnIndex === 0;
					const isLastColumn = columnIndex === columns.length - 1;
					const headerPadding = isFirstColumn
						? 'pl-7 pr-3'
						: isLastColumn
							? 'pl-3 pr-7'
							: 'px-3';
					const cellPadding = isFirstColumn
						? 'pl-7 pr-3'
						: isLastColumn
							? 'pl-3 pr-7'
							: 'px-3';
					return (
						<div
							key={column.key}
							className={`${column.width} inline-flex flex-col justify-start items-start`}
							role="column"
						>
							<div
								className={`w-full h-10 ${headerPadding} border-b border-black/5 flex flex-col justify-center ${column.key === 'feeDue' ? 'items-end' : 'items-start'}`}
								role="columnheader"
								aria-label={column.label}
							>
								<div
									className={`${column.align} text-sm font-medium leading-none flex items-center gap-1`}
								>
									{column.label}
									{/* Show disclaimer tooltip for Rate column on current processor for flat-tiered statements */}
									{showRateDisclaimer &&
										isCurrentProcessor &&
										column.key === 'rate' && (
											<span className="relative group cursor-help">
												<svg
													className="w-3.5 h-3.5 text-amber-500"
													fill="currentColor"
													viewBox="0 0 20 20"
												>
													<path
														fillRule="evenodd"
														d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
														clipRule="evenodd"
													/>
												</svg>
												<span className="absolute hidden group-hover:block z-50 w-56 p-2 top-full left-0 mt-1 bg-gray-900 text-white text-xs font-normal rounded shadow-lg whitespace-normal pointer-events-none">
													Rates displayed are aggregate averages based on total
													volume and fees.
												</span>
											</span>
										)}
								</div>
							</div>
							{!showLoader &&
								data.map((item, index) => {
									const hasChildren =
										showChildren && item.children && item.children.length > 0;
									const isExpanded = expandedRows.has(item.line_item_id);
									// Use unique identifier for hover to prevent duplicate line_item_id issues
									// Use item.id (database UUID) if available, otherwise use line_item_id with index for uniqueness
									const uniqueRowId =
										item.id || `${item.line_item_id}_${index}`;
									const isItemHovered =
										hoveredRow === uniqueRowId ||
										hoveredRow === item.line_item_id ||
										hoveredRow === item.line_item_id_ic_plus;
									// Determine which line_item_id to use based on processor
									// For IC: use line_item_id (or line_item_id_ic_plus if line_item_id is empty)
									// For IC+: use line_item_id_ic_plus if different, else use line_item_id
									const actualLineItemId = isCurrentProcessor
										? item.line_item_id || item.line_item_id_ic_plus || ''
										: item.line_item_id_ic_plus || item.line_item_id || '';
									// Check if this specific cell is being edited
									const processorKey = isCurrentProcessor ? 'ic' : 'icplus';
									const cellId = `${actualLineItemId}_${processorKey}_${column.key}`;
									const isCellEditing =
										enableEditing && editingCellId === cellId;
									const isEditableColumn =
										enableEditing &&
										isCellEditing &&
										['rate', 'fee', 'volume', 'count'].includes(column.key);

									// Get anomaly styling for this specific cell if it has an anomaly
									const validation =
										item.validation ?? (item as any).validation_metadata;
									const anomalyCellStyle =
										(column.key === 'rate' || column.key === 'feeDue') &&
										isCurrentProcessor
											? getAnomalyCellStyle(validation)
											: { backgroundColor: '', tooltip: undefined };

									// Check if this is the winning fee (green text)
									const isWinner =
										column.key === 'feeDue' &&
										isWinningFee(item, isCurrentProcessor);

									// Check if this field has been changed (for yellow highlighting)
									// Check both: currently being edited (hasFieldChanged) OR already saved as modified (modified_fields)
									const fieldKey = column.key as
										| 'rate'
										| 'fee'
										| 'volume'
										| 'count'
										| 'feeDue';
									const isCurrentlyEditing =
										hasFieldChanged &&
										['rate', 'fee', 'volume', 'count', 'feeDue'].includes(
											column.key,
										)
											? hasFieldChanged(
													actualLineItemId,
													fieldKey,
													item,
													isCurrentProcessor,
												)
											: false;

									// For saved modifications, check if this specific field is in the modified_fields list
									// Map column keys to backend field names if necessary
									let backendField = column.key;
									if (column.key === 'fee')
										backendField = 'per_transaction_fee';
									if (column.key === 'count')
										backendField = 'transaction_count';
									// feeDue is usually 'amount' in backend but we track changes to 'amount'
									if (column.key === 'feeDue') backendField = 'amount';

									const modifiedFields = isCurrentProcessor
										? item.modified_fields_ic
										: item.modified_fields_ic_plus;
									const isSavedModification =
										modifiedFields?.includes(backendField) || false;
									const isFieldChanged =
										isCurrentlyEditing || isSavedModification;

									// Priority: anomaly style > changed (amber/yellow) > winner (green) > default (black)
									const textColor =
										anomalyCellStyle.textColorClass ||
										(isFieldChanged ? 'text-amber-600 font-semibold' : '') ||
										(isWinner ? WIN_TEXT : 'text-black');

									// Get editable value if in edit mode
									let editableValue: string | number | undefined;
									let editableField: string | undefined;
									if (isEditableColumn && onEditChange && getCurrentValue) {
										const isOtherService = !('rate' in (item as any).ic);
										const dataSource = isCurrentProcessor
											? isOtherService
												? (item as UnifiedOtherService).ic
												: (item as UnifiedFeeItem).ic
											: isOtherService
												? (item as UnifiedOtherService).icPlus
												: (item as UnifiedFeeItem).icPlus;

										if (column.key === 'rate') {
											editableField = 'rate';
											const originalRate = isOtherService
												? undefined
												: parseFloat(
														(dataSource as any).rate?.replace('%', '') || '0',
													) / 100;
											const currentRate = getCurrentValue(
												actualLineItemId,
												'rate',
												originalRate,
												isCurrentProcessor,
											);
											// For rate, use the current value if it's a string (already in percentage format), otherwise convert
											if (currentRate !== undefined) {
												if (typeof currentRate === 'string') {
													editableValue = currentRate;
												} else if (typeof currentRate === 'number') {
													editableValue = (currentRate * 100).toFixed(4); // Convert to percentage string
												} else {
													editableValue =
														originalRate !== undefined
															? (originalRate * 100).toFixed(4)
															: '0.0000';
												}
											} else {
												editableValue =
													originalRate !== undefined
														? (originalRate * 100).toFixed(4)
														: '0.0000';
											}
										} else if (column.key === 'fee') {
											editableField = 'per_transaction_fee';
											const originalFee =
												(dataSource as any).perTrans ??
												(dataSource as any).fee ??
												0;
											// Use ?? (not ||) so a deliberately-cleared field ('' from the
											// edit state) is preserved instead of snapping back to the
											// original — otherwise the user can never delete the value.
											editableValue =
												getCurrentValue(
													actualLineItemId,
													'per_transaction_fee',
													originalFee,
													isCurrentProcessor,
												)?.toString() ?? originalFee.toString();
										} else if (column.key === 'volume') {
											editableField = 'volume';
											const originalVolume = parseFloat(
												(dataSource as any).volume
													?.toString()
													.replace(/[$,]/g, '') || '0',
											);
											// ?? (not ||) so a cleared field ('') is kept, not reverted.
											editableValue =
												getCurrentValue(
													actualLineItemId,
													'volume',
													originalVolume,
													isCurrentProcessor,
												)?.toString() ??
												originalVolume?.toString() ??
												'0';
										} else if (column.key === 'count') {
											editableField = 'transaction_count';
											const originalCount = (dataSource as any).count || 0;
											editableValue =
												getCurrentValue(
													actualLineItemId,
													'transaction_count',
													originalCount,
													isCurrentProcessor,
												)?.toString() || originalCount.toString();
										}
									}

									// Check if this cell is hovered (for showing pencil icon)
									// For IC+, check both line_item_id and line_item_id_ic_plus for hover
									// Parent rows (items with children) should not be editable
									const hoveredLineItemId = isCurrentProcessor
										? item.line_item_id
										: item.line_item_id_ic_plus || item.line_item_id;
									const isCellHovered =
										(hoveredRow === uniqueRowId ||
											hoveredRow === item.line_item_id ||
											hoveredRow === item.line_item_id_ic_plus ||
											hoveredRow === hoveredLineItemId) &&
										enableEditing &&
										['rate', 'fee', 'volume', 'count'].includes(column.key) &&
										!isCellEditing &&
										!hasChildren;

									return (
										<React.Fragment key={index}>
											<div
												className={`w-full h-10 min-w-10 ${cellPadding} border-b border-black/5 flex justify-between items-center gap-2 transition-colors bg-gradient-to-r from-gray-50/80 to-transparent} ${anomalyCellStyle.backgroundColor} ${anomalyCellStyle.tooltip ? 'relative group overflow-visible' : 'relative overflow-visible'}`}
												role="cell"
												aria-label={`${column.label}: ${getCellValue(item, column.key, isCurrentProcessor)}`}
												onMouseEnter={() => {
													// Use unique identifier for hover to prevent duplicate line_item_id issues
													onHoverChange(uniqueRowId);
												}}
												onMouseLeave={() => onHoverChange(null)}
											>
												{isEditableColumn &&
												editableField &&
												editableValue !== undefined ? (
													<div
														className={`absolute left-0 top-0 z-20 h-full min-w-[140px] ${cellPadding} flex items-center gap-1 bg-white shadow-md rounded-sm border border-indigo-100`}
													>
														<input
															type="text"
															inputMode={column.key === 'count' ? 'numeric' : 'decimal'}
															value={editableValue}
															onChange={(e) => {
																if (onEditChange && editableField) {
																	if (column.key === 'count') {
																		const value = parseInt(e.target.value, 10);
																		onEditChange(
																			actualLineItemId,
																			editableField as keyof LineItemEditState,
																			isNaN(value) ? 0 : value,
																		);
																	} else {
																		// For rate, amount, fee_due, volume: store as string during editing
																		const value = e.target.value;
																		if (
																			value === '' ||
																			/^-?\d*\.?\d*$/.test(value)
																		) {
																			onEditChange(
																				actualLineItemId,
																				editableField as keyof LineItemEditState,
																				value,
																			);
																		}
																	}
																}
															}}
															className={`flex-1 ${column.align} text-xs border border-indigo-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white`}
															placeholder={
																getCellValue(
																	item,
																	column.key,
																	isCurrentProcessor,
																) as string
															}
															step={column.key === 'rate' ? '0.0001' : '0.01'}
															autoFocus
															onKeyDown={(e) => {
																if (e.key === 'Enter' && onSaveEdit) {
																	e.preventDefault();
																	// Don't save an empty value.
																	if (
																		typeof editableValue === 'string' &&
																		editableValue.trim() === ''
																	)
																		return;
																	onSaveEdit();
																} else if (e.key === 'Escape' && onCancelEdit) {
																	e.preventDefault();
																	onCancelEdit();
																}
															}}
														/>
														{onSaveEdit && onCancelEdit ? (
															<div className="flex items-center gap-0.5 flex-shrink-0 ml-1 w-[52px]">
																<button
																	onClick={(e) => {
																		e.stopPropagation();
																		onSaveEdit();
																	}}
																	disabled={
																		savingLineItemId === actualLineItemId ||
																		(typeof editableValue === 'string' &&
																			editableValue.trim() === '')
																	}
																	className="p-1 rounded text-green-600 hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
																	title="Save"
																	aria-label="Save changes"
																>
																	<Check className="w-4 h-4" />
																</button>
																<button
																	onClick={(e) => {
																		e.stopPropagation();
																		onCancelEdit();
																	}}
																	disabled={
																		savingLineItemId === actualLineItemId
																	}
																	className="p-1 rounded text-red-500 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
																	title="Cancel"
																	aria-label="Cancel editing"
																>
																	<X className="w-4 h-4" />
																</button>
															</div>
														) : null}
													</div>
												) : (
													<div
														className={`flex-1 ${column.align} ${textColor} text-xs font-normal leading-none ${column.key === 'feeDue' ? 'flex flex-col items-end' : 'flex items-center'} ${column.key === 'feeDue' ? 'justify-end' : 'justify-between'} gap-2`}
													>
														{column.key === 'feeDue' ? (
															<>
																{(() => {
																	// Calculate expected fee due if any field is being edited for this line item
																	const editState =
																		lineItemEdits[actualLineItemId];
																	if (editState) {
																		const isOtherService = !(
																			'rate' in (item as any).ic
																		);
																		const dataSource = isCurrentProcessor
																			? isOtherService
																				? (item as UnifiedOtherService).ic
																				: (item as UnifiedFeeItem).ic
																			: isOtherService
																				? (item as UnifiedOtherService).icPlus
																				: (item as UnifiedFeeItem).icPlus;

																		const count =
																			(dataSource as any).count || 0;
																		let currentRate = 0;
																		if (editState.rate !== undefined) {
																			currentRate =
																				typeof editState.rate === 'string'
																					? parseFloat(editState.rate) / 100
																					: editState.rate;
																		} else {
																			currentRate = isOtherService
																				? 0
																				: parseFloat(
																						(dataSource as any).rate?.replace(
																							'%',
																							'',
																						) || '0',
																					) / 100;
																		}
																		const currentFee =
																			editState.per_transaction_fee !==
																			undefined
																				? editState.per_transaction_fee
																				: (dataSource as any).perTrans ??
																					(dataSource as any).fee ??
																					0;
																		const currentVolume =
																			editState.volume !== undefined
																				? editState.volume
																				: isOtherService
																					? 0
																					: parseFloat(
																							(dataSource as any).volume
																								?.toString()
																								.replace(/[$,]/g, '') || '0',
																						);

																		const calculatedFeeDue =
																			count * currentFee +
																			currentRate * currentVolume;
																		const originalFeeDue = dataSource.feeDue;
																		const isDifferent =
																			Math.abs(
																				calculatedFeeDue - originalFeeDue,
																			) > 0.01;

																		if (isDifferent) {
																			return (
																				<>
																					<span>
																						{formatCurrency(calculatedFeeDue)}
																					</span>
																					<span className="text-[10px] text-gray-500 line-through">
																						{formatCurrency(originalFeeDue)}
																					</span>
																				</>
																			);
																		}
																	}
																	return (
																		<span>
																			{getCellValue(
																				item,
																				column.key,
																				isCurrentProcessor,
																			)}
																		</span>
																	);
																})()}
															</>
														) : (
															<>
																<div className="flex items-center gap-1 flex-1 min-w-0">
																	<span className={textColor}>
																		{getCellValue(
																			item,
																			column.key,
																			isCurrentProcessor,
																		)}
																	</span>
																	{(() => {
																		const fieldHasUserEdit = isFieldUserEdited(
																			item,
																			column.key,
																			isCurrentProcessor,
																		);
																		const fieldOriginalValue = fieldHasUserEdit
																			? getOriginalValue(
																					item,
																					column.key,
																					isCurrentProcessor,
																				)
																			: null;
																		const fieldOriginalValueFormatted =
																			fieldOriginalValue !== null
																				? formatOriginalValue(
																						fieldOriginalValue,
																						column.key,
																					)
																				: null;
																		return fieldHasUserEdit &&
																			fieldOriginalValueFormatted ? (
																			<span
																				className="text-xs text-indigo-500 cursor-help flex-shrink-0"
																				title={`Original: ${fieldOriginalValueFormatted}`}
																			>
																				✏️
																			</span>
																		) : null;
																	})()}
																</div>
																<div className="flex-shrink-0 w-[52px] flex justify-end">
																	{isCellHovered && onStartEdit && (
																		<button
																			onClick={(e) => {
																				e.stopPropagation();
																				onStartEdit(
																					item.line_item_id,
																					item,
																					isCurrentProcessor,
																					column.key,
																				);
																			}}
																			className="p-0.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors flex-shrink-0"
																			title="Edit"
																			aria-label={`Edit ${column.label}`}
																		>
																			<Pencil className="w-3 h-3" />
																		</button>
																	)}
																</div>
															</>
														)}
													</div>
												)}
												{anomalyCellStyle.tooltip && (
													<div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-max max-w-xs -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100">
														{anomalyCellStyle.tooltip}
													</div>
												)}
											</div>
											{/* Render children cells if expanded */}
											{hasChildren &&
												isExpanded &&
												item.children!.map((child, childIndex) => {
													const isLastChild =
														childIndex === item.children!.length - 1;
													const isChildHovered =
														hoveredRow === child.line_item_id;
													// Determine which line_item_id to use for child based on processor
													const childActualLineItemId = isCurrentProcessor
														? child.line_item_id ||
															child.line_item_id_ic_plus ||
															''
														: child.line_item_id_ic_plus ||
															child.line_item_id ||
															'';
													const childProcessorKey = isCurrentProcessor
														? 'ic'
														: 'icplus';
													const childCellId = `${childActualLineItemId}_${childProcessorKey}_${column.key}`;
													const isChildEditing =
														enableEditing && editingCellId === childCellId;

													// Force re-render by including data values in key
													const childDataForKey = isCurrentProcessor
														? (child as any).ic
														: (child as any).icPlus;
													const childKeyData = `${childDataForKey?.fee || 0}-${childDataForKey?.rate || 0}-${childDataForKey?.volume || 0}`;
													const childKey = `${child.line_item_id}-${column.key}-${childKeyData}`;
													const isChildEditableColumn =
														enableEditing &&
														isChildEditing &&
														['rate', 'fee', 'volume', 'count'].includes(
															column.key,
														);
													const childValidation =
														child.validation ??
														(child as any).validation_metadata;
													const childAnomalyCellStyle =
														(column.key === 'rate' ||
															column.key === 'feeDue') &&
														isCurrentProcessor
															? getAnomalyCellStyle(childValidation)
															: { backgroundColor: '', tooltip: undefined };

													// Check if child has winning fee (green text)
													const isChildWinner =
														column.key === 'feeDue' &&
														isWinningFee(child, isCurrentProcessor);

													// Check if this child field has been changed (for yellow highlighting)
													const childFieldKey = column.key as
														| 'rate'
														| 'fee'
														| 'volume'
														| 'count'
														| 'feeDue';
													const isChildCurrentlyEditing =
														hasFieldChanged &&
														[
															'rate',
															'fee',
															'volume',
															'count',
															'feeDue',
														].includes(column.key)
															? hasFieldChanged(
																	childActualLineItemId,
																	childFieldKey,
																	child,
																	isCurrentProcessor,
																)
															: false;

													// For saved modifications, check if this specific field is in the modified_fields list
													// Map column keys to backend field names if necessary
													let childBackendField = column.key;
													if (column.key === 'fee')
														childBackendField = 'per_transaction_fee';
													if (column.key === 'count')
														childBackendField = 'transaction_count';
													if (column.key === 'feeDue')
														childBackendField = 'amount';

													const childModifiedFields = isCurrentProcessor
														? child.modified_fields_ic
														: child.modified_fields_ic_plus;
													const isChildSavedModification =
														childModifiedFields?.includes(childBackendField) ||
														false;
													const isChildFieldChanged =
														isChildCurrentlyEditing || isChildSavedModification;

													// Priority: anomaly style > changed (amber/yellow) > winner (green) > default (gray)
													const childTextColor =
														childAnomalyCellStyle.textColorClass ||
														(isChildFieldChanged
															? 'text-amber-600 font-semibold'
															: '') ||
														(isChildWinner ? WIN_TEXT : 'text-gray-700');

													// Get editable value if in edit mode
													let childEditableValue: string | number | undefined;
													let childEditableField: string | undefined;
													if (
														isChildEditableColumn &&
														onEditChange &&
														getCurrentValue
													) {
														const isOtherService = !(
															'rate' in (child as any).ic
														);
														const childDataSource = isCurrentProcessor
															? isOtherService
																? (child as UnifiedOtherService).ic
																: (child as UnifiedFeeItem).ic
															: isOtherService
																? (child as UnifiedOtherService).icPlus
																: (child as UnifiedFeeItem).icPlus;

														if (column.key === 'rate') {
															childEditableField = 'rate';
															const originalRate = isOtherService
																? undefined
																: parseFloat(
																		(childDataSource as any).rate?.replace(
																			'%',
																			'',
																		) || '0',
																	) / 100;
															const currentRate = getCurrentValue(
																childActualLineItemId,
																'rate',
																originalRate,
																isCurrentProcessor,
															);
															// For rate, use the current value if it's a string (already in percentage format), otherwise convert
															if (currentRate !== undefined) {
																if (typeof currentRate === 'string') {
																	childEditableValue = currentRate;
																} else if (typeof currentRate === 'number') {
																	childEditableValue = (
																		currentRate * 100
																	).toFixed(4); // Convert to percentage string
																} else {
																	childEditableValue =
																		originalRate !== undefined
																			? (originalRate * 100).toFixed(4)
																			: '0.0000';
																}
															} else {
																childEditableValue =
																	originalRate !== undefined
																		? (originalRate * 100).toFixed(4)
																		: '0.0000';
															}
														} else if (column.key === 'fee') {
															childEditableField = 'per_transaction_fee';
															const originalFee =
																(childDataSource as any).perTrans ??
																(childDataSource as any).fee ??
																0;
															// ?? (not ||) so a cleared field ('') is kept, not reverted.
															childEditableValue =
																getCurrentValue(
																	childActualLineItemId,
																	'per_transaction_fee',
																	originalFee,
																	isCurrentProcessor,
																)?.toString() ?? originalFee.toString();
														} else if (column.key === 'volume') {
															childEditableField = 'volume';
															const originalVolume = parseFloat(
																(childDataSource as any).volume
																	?.toString()
																	.replace(/[$,]/g, '') || '0',
															);
															// ?? (not ||) so a cleared field ('') is kept, not reverted.
															childEditableValue =
																getCurrentValue(
																	childActualLineItemId,
																	'volume',
																	originalVolume,
																	isCurrentProcessor,
																)?.toString() ??
																originalVolume?.toString() ??
																'0';
														} else if (column.key === 'count') {
															childEditableField = 'transaction_count';
															const originalCount =
																(childDataSource as any).count || 0;
															childEditableValue =
																getCurrentValue(
																	childActualLineItemId,
																	'transaction_count',
																	originalCount,
																	isCurrentProcessor,
																)?.toString() || originalCount.toString();
														}
													}

													// Check if this child cell is hovered (for showing pencil icon)
													// For IC+, check both line_item_id and line_item_id_ic_plus for hover
													const childHoveredLineItemId = isCurrentProcessor
														? child.line_item_id
														: child.line_item_id_ic_plus || child.line_item_id;
													const isChildCellHovered =
														(hoveredRow === child.line_item_id ||
															hoveredRow === child.line_item_id_ic_plus ||
															hoveredRow === childHoveredLineItemId) &&
														enableEditing &&
														['rate', 'fee', 'volume', 'count'].includes(
															column.key,
														) &&
														!isChildEditing;

													return (
														<div
															key={childKey}
															className={`w-full h-10 min-w-10 ${cellPadding} border-b inline-flex justify-between items-center gap-2 transition-colors bg-gradient-to-r from-gray-50/80 to-transparent
														 ${isLastChild ? 'border-gray-200/50' : 'border-gray-100/50'} ${childAnomalyCellStyle.backgroundColor} ${childAnomalyCellStyle.tooltip ? 'relative group overflow-visible' : 'relative overflow-visible'}`}
															role="cell"
															aria-label={`Child ${column.label}: ${getCellValue(child, column.key, isCurrentProcessor)}`}
															onMouseEnter={() => {
																// For IC+, use the appropriate line item ID for hover
																const childHoverId = isCurrentProcessor
																	? child.line_item_id
																	: child.line_item_id_ic_plus ||
																		child.line_item_id;
																onHoverChange(childHoverId);
															}}
															onMouseLeave={() => onHoverChange(null)}
														>
															{isChildEditableColumn &&
															childEditableField &&
															childEditableValue !== undefined ? (
																<div
																	className={`absolute left-0 top-0 z-20 h-full min-w-[140px] ${cellPadding} flex items-center gap-1 bg-white shadow-md rounded-sm border border-indigo-100`}
																>
																	<input
																		type="text"
																		inputMode={column.key === 'count' ? 'numeric' : 'decimal'}
																		value={childEditableValue}
																		onChange={(e) => {
																			if (onEditChange && childEditableField) {
																				if (column.key === 'count') {
																					const value = parseInt(e.target.value, 10);
																					onEditChange(
																						childActualLineItemId,
																						childEditableField as keyof LineItemEditState,
																						isNaN(value) ? 0 : value,
																					);
																				} else {
																					// For rate, amount, fee_due, volume: store as string during editing
																					const value = e.target.value;
																					if (
																						value === '' ||
																						/^-?\d*\.?\d*$/.test(value)
																					) {
																						onEditChange(
																							childActualLineItemId,
																							childEditableField as keyof LineItemEditState,
																							value,
																						);
																					}
																				}
																			}
																		}}
																		className={`flex-1 ${column.align} text-xs border border-indigo-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white`}
																		placeholder={
																			getCellValue(
																				child,
																				column.key,
																				isCurrentProcessor,
																			) as string
																		}
																		step={
																			column.key === 'rate' ? '0.0001' : '0.01'
																		}
																		autoFocus
																		onKeyDown={(e) => {
																			if (e.key === 'Enter' && onSaveEdit) {
																				e.preventDefault();
																				// Don't save an empty value.
																				if (
																					typeof childEditableValue ===
																						'string' &&
																					childEditableValue.trim() === ''
																				)
																					return;
																				onSaveEdit();
																			} else if (
																				e.key === 'Escape' &&
																				onCancelEdit
																			) {
																				e.preventDefault();
																				onCancelEdit();
																			}
																		}}
																	/>
																	{onSaveEdit && onCancelEdit ? (
																		<div className="flex items-center gap-0.5 flex-shrink-0 ml-1 w-[52px]">
																			<button
																				onClick={(e) => {
																					e.stopPropagation();
																					onSaveEdit();
																				}}
																				disabled={
																					savingLineItemId ===
																						childActualLineItemId ||
																					(typeof childEditableValue ===
																						'string' &&
																						childEditableValue.trim() ===
																							'')
																				}
																				className="p-1 rounded text-green-600 hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
																				title="Save"
																				aria-label="Save changes"
																			>
																				<Check className="w-4 h-4" />
																			</button>
																			<button
																				onClick={(e) => {
																					e.stopPropagation();
																					onCancelEdit();
																				}}
																				disabled={
																					savingLineItemId ===
																					childActualLineItemId
																				}
																				className="p-1 rounded text-red-500 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
																				title="Cancel"
																				aria-label="Cancel editing"
																			>
																				<X className="w-4 h-4" />
																			</button>
																		</div>
																	) : null}
																</div>
															) : (
																<div
																	className={`flex-1 ${column.align} ${childTextColor} text-xs font-medium leading-none ${column.key === 'feeDue' ? 'flex flex-col items-end' : 'flex items-center'} ${column.key === 'feeDue' ? 'justify-end' : 'justify-between'} gap-2`}
																>
																	{column.key === 'feeDue' ? (
																		<>
																			{(() => {
																				// Calculate expected fee due if any field is being edited for this child line item
																				const editState =
																					lineItemEdits[childActualLineItemId];
																				if (editState) {
																					const isOtherService = !(
																						'rate' in (child as any).ic
																					);
																					const childDataSource =
																						isCurrentProcessor
																							? isOtherService
																								? (child as UnifiedOtherService)
																										.ic
																								: (child as UnifiedFeeItem).ic
																							: isOtherService
																								? (child as UnifiedOtherService)
																										.icPlus
																								: (child as UnifiedFeeItem)
																										.icPlus;

																					const count =
																						(childDataSource as any).count || 0;
																					let currentRate = 0;
																					if (editState.rate !== undefined) {
																						currentRate =
																							typeof editState.rate === 'string'
																								? parseFloat(editState.rate) /
																									100
																								: editState.rate;
																					} else {
																						currentRate = isOtherService
																							? 0
																							: parseFloat(
																									(
																										childDataSource as any
																									).rate?.replace('%', '') ||
																										'0',
																								) / 100;
																					}
																					const currentFee =
																						editState.per_transaction_fee !==
																						undefined
																							? editState.per_transaction_fee
																							: (childDataSource as any)
																									.perTrans ??
																								(childDataSource as any).fee ??
																								0;
																					const currentVolume =
																						editState.volume !== undefined
																							? editState.volume
																							: isOtherService
																								? 0
																								: parseFloat(
																										(
																											childDataSource as any
																										).volume
																											?.toString()
																											.replace(/[$,]/g, '') ||
																											'0',
																									);

																					const calculatedFeeDue =
																						count * currentFee +
																						currentRate * currentVolume;
																					const originalFeeDue =
																						childDataSource.feeDue;
																					const isDifferent =
																						Math.abs(
																							calculatedFeeDue - originalFeeDue,
																						) > 0.01;

																					if (isDifferent) {
																						return (
																							<>
																								<span>
																									{formatCurrency(
																										calculatedFeeDue,
																									)}
																								</span>
																								<span className="text-[10px] text-gray-500 line-through">
																									{formatCurrency(
																										originalFeeDue,
																									)}
																								</span>
																							</>
																						);
																					}
																				}
																				return (
																					<span>
																						{getCellValue(
																							child,
																							column.key,
																							isCurrentProcessor,
																						)}
																					</span>
																				);
																			})()}
																		</>
																	) : (
																		<>
																			<span>
																				{getCellValue(
																					child,
																					column.key,
																					isCurrentProcessor,
																				)}
																			</span>
																			{isChildCellHovered && onStartEdit && (
																				<button
																					onClick={(e) => {
																						e.stopPropagation();
																						onStartEdit(
																							child.line_item_id,
																							child,
																							isCurrentProcessor,
																							column.key,
																						);
																					}}
																					className="p-0.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors flex-shrink-0"
																					title="Edit"
																					aria-label={`Edit ${column.label}`}
																				>
																					<Pencil className="w-3 h-3" />
																				</button>
																			)}
																		</>
																	)}
																</div>
															)}
															{childAnomalyCellStyle.tooltip && (
																<div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-max max-w-xs -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100">
																	{childAnomalyCellStyle.tooltip}
																</div>
															)}
														</div>
													);
												})}
										</React.Fragment>
									);
								})}
						</div>
					);
				})}
			</div>
			<div
				className="w-full h-10 pr-7 bg-black/5 border-b border-black/5 flex flex-col justify-center items-end"
				role="cell"
				aria-label={`Total: ${formatCurrency(total)}`}
			>
				<div
					className={`${columns[columns.length - 1].align} text-sm font-medium leading-none`}
				>
					{formatCurrency(total)}
				</div>
			</div>
		</div>
	);
};

interface AnalysisTableProps {
	title: string;
	data: UnifiedFeeItem[] | UnifiedOtherService[];
	columns: string[];
	processingMonth: string;
	fileId: string;
	processorNamePlus?: string;
	processorName?: string;
	isLoading?: boolean;
	isFetching?: boolean;
	showChildren?: boolean;
	enableEditing?: boolean;
	onLineItemUpdated?: () => void;
	activeId?: string | null;
	draggedFromCategory?: string | null;
	draggedItemHasChildren?: boolean; // Whether the currently dragged item has children
	sectionKey?: string;
	expandedRows: Set<string>;
	onToggleRow: (lineItemId: string) => void;
	recentlyDroppedIds?: Set<string>; // IDs of items that were recently dropped (for 10-second highlight)
	// Undo functionality props
	onUndoDrop?: (item?: UnifiedFeeItem | UnifiedOtherService) => void; // Callback to undo the drop (item optional for persistent undo)
	undoableItemIds?: Set<string>; // Set of item IDs that can be undone (from dropHistory stack)
	isUndoing?: boolean; // Whether an undo operation is in progress
	// Flat-tiered rate disclaimer
	showRateDisclaimer?: boolean; // Show disclaimer tooltip on Rate column for flat-tiered statements
	isCurrentProcessor: boolean; // Whether this table represents IC (true) or IC+ (false)
	// View mode change callback - called when user makes an edit to switch to user_edits view
	onViewModeChange?: (
		view: 'default' | 'user_edits' | 'customized_quote',
	) => void;
}

const AnalysisTable: React.FC<AnalysisTableProps> = ({
	title,
	data,
	columns,
	processingMonth,
	fileId,
	processorNamePlus,
	processorName,
	isLoading = false,
	isFetching = false,
	showChildren = true,
	enableEditing = false,
	onLineItemUpdated,
	activeId: externalActiveId = null,
	draggedFromCategory: externalDraggedFromCategory = null,
	draggedItemHasChildren: externalDraggedItemHasChildren = false,
	sectionKey,
	expandedRows,
	onToggleRow,
	recentlyDroppedIds = new Set(),
	onUndoDrop,
	undoableItemIds = new Set<string>(),
	isUndoing = false,
	showRateDisclaimer = false,
	isCurrentProcessor,
	onViewModeChange,
}) => {
	// Use the loading states from props, or fall back to data presence check
	const showLoader = isLoading || isFetching;

	const [hoveredRow, setHoveredRow] = useState<string | null>(null);
	const flaggedLines = useFlaggedLines();
	// expandedRows state lifted to parent
	const [localFeedback, setLocalFeedback] = useState<Record<string, any>>({});
	const [feedbackModal, setFeedbackModal] = useState<{
		isOpen: boolean;
		ratingType: 'THUMBS_UP' | 'THUMBS_DOWN';
		lineItemId: string;
		showCommentModal: boolean;
	}>({
		isOpen: false,
		ratingType: 'THUMBS_UP',
		lineItemId: '',
		showCommentModal: false,
	});

	// Edit state management - now cell-specific
	// Format: `${lineItemId}_${processor}_${columnKey}` e.g., "123_ic_rate" or "456_icplus_volume"
	const [editingCellId, setEditingCellId] = useState<string | null>(null);
	const [lineItemEdits, setLineItemEdits] = useState<
		Record<string, LineItemEditState>
	>({});
	const [savingLineItemId, setSavingLineItemId] = useState<string | null>(null);
	const [isRefetchingAfterSave, setIsRefetchingAfterSave] = useState(false);

	// Sorting state
	const [sortConfig, setSortConfig] = useState<{
		column: string | null;
		direction: 'asc' | 'desc';
		isCurrentProcessor: boolean;
	}>({
		column: null,
		direction: 'asc',
		isCurrentProcessor: true,
	});

	// Use external drag state from parent (AnalysisStep)
	const activeId = externalActiveId;
	const draggedFromCategory = externalDraggedFromCategory;

	// Audit log modal state
	const [auditLogModal, setAuditLogModal] = useState<{
		isOpen: boolean;
		lineItemId: string;
		lineItemDescription: string;
	}>({
		isOpen: false,
		lineItemId: '',
		lineItemDescription: '',
	});

	// Line item update mutation
	const [updateLineItem] = useUpdateLineItemMutation();

	// Sorting handler
	const handleSort = useCallback(
		(columnKey: string, isCurrentProcessor: boolean) => {
			// Only allow sorting on sortable columns
			if (!['rate', 'fee', 'feeDue', 'volume'].includes(columnKey)) return;

			setSortConfig((prev) => {
				if (
					prev.column === columnKey &&
					prev.isCurrentProcessor === isCurrentProcessor
				) {
					// Toggle direction
					return {
						column: columnKey,
						direction: prev.direction === 'asc' ? 'desc' : 'asc',
						isCurrentProcessor,
					};
				} else {
					// New column or different processor, default to ascending
					return {
						column: columnKey,
						direction: 'asc',
						isCurrentProcessor,
					};
				}
			});
		},
		[],
	);

	// Sorting removed to prevent items from being lost or mismatched when moved
	// Items will appear in the order they are received from the backend
	const sortedData = data;

	// Edit handlers - now cell-specific
	// Start editing a specific cell (processor + column)
	const handleStartEdit = useCallback(
		(
			lineItemId: string,
			item: UnifiedFeeItem | UnifiedOtherService,
			isCurrentProcessor: boolean,
			columnKey: string,
		) => {
			// Don't allow editing while refetching after save
			if (isRefetchingAfterSave) {
				return;
			}

			// Determine which line_item_id to use based on processor
			// For IC: use line_item_id (or line_item_id_ic_plus if line_item_id is empty)
			// For IC+: use line_item_id_ic_plus if different, else use line_item_id
			const actualLineItemId = isCurrentProcessor
				? item.line_item_id || item.line_item_id_ic_plus || lineItemId
				: item.line_item_id_ic_plus || item.line_item_id || lineItemId;

			// Create cell ID: `${lineItemId}_${processor}_${columnKey}`
			const processorKey = isCurrentProcessor ? 'ic' : 'icplus';
			const cellId = `${actualLineItemId}_${processorKey}_${columnKey}`;
			setEditingCellId(cellId);

			// Get the data source based on processor type
			const isOtherService = !('rate' in (item as any).ic);
			const dataSource = isCurrentProcessor
				? isOtherService
					? (item as UnifiedOtherService).ic
					: (item as UnifiedFeeItem).ic
				: isOtherService
					? (item as UnifiedOtherService).icPlus
					: (item as UnifiedFeeItem).icPlus;

			// Extract numeric values from formatted strings
			const parseRate = (rateStr: string): number => {
				if (!rateStr || rateStr === '-') return 0;
				// Remove % and parse (e.g., "0.14%" -> 0.0014)
				const num = parseFloat(rateStr.replace('%', '')) / 100;
				return isNaN(num) ? 0 : num;
			};

			const parseCurrency = (currencyStr: string): number => {
				if (!currencyStr || currencyStr === '-') return 0;
				// Remove $ and commas (e.g., "$1,234.56" -> 1234.56)
				const num = parseFloat(currencyStr.replace(/[$,]/g, ''));
				return isNaN(num) ? 0 : num;
			};

			// Initialize edit state for this line item if not exists
			// CRITICAL: Use processor-specific key to prevent edits from one side appearing on the other
			const processorSpecificKey = `${actualLineItemId}_${processorKey}`;
			setLineItemEdits((prev) => {
				const rateValue = isOtherService
					? undefined
					: parseRate((dataSource as any).rate || '0%');

				const existing = prev[processorSpecificKey] || {
					lineItemId: actualLineItemId,
					description: item.type,
					amount: dataSource.feeDue,
					rate: rateValue,
					per_transaction_fee:
						(dataSource as any).perTrans ?? (dataSource as any).fee ?? 0,
					volume: isOtherService
						? undefined
						: parseCurrency((dataSource as any).volume?.toString() || '0'),
					transaction_count: (dataSource as any).count || 0,
				};

				return {
					...prev,
					[processorSpecificKey]: existing,
				};
			});
		},
		[isRefetchingAfterSave],
	);

	const handleCancelEdit = useCallback(() => {
		if (editingCellId) {
			// Extract lineItemId from cellId (format: `${lineItemId}_${processor}_${columnKey}`)
			const parts = editingCellId.split('_');
			if (parts.length >= 3) {
				const lineItemId = parts[0];
				setLineItemEdits((prev) => {
					const newEdits = { ...prev };
					delete newEdits[lineItemId];
					return newEdits;
				});
			}
		}
		setEditingCellId(null);
	}, [editingCellId]);

	const handleEditChange = useCallback(
		(
			lineItemId: string,
			field: keyof LineItemEditState,
			value: string | number,
		) => {
			// CRITICAL FIX: lineItemEdits must be processor-specific to prevent edits from one side
			// appearing on the other side. We need to extract the processor key from the editingCellId.
			// The lineItemId passed here should actually be the processor-specific key.
			// For now, we'll use the editingCellId to determine the processor-specific key.
			if (!editingCellId) {
				// Fallback: if no editingCellId, use lineItemId (backward compatibility)
				setLineItemEdits((prev) => ({
					...prev,
					[lineItemId]: {
						...prev[lineItemId],
						[field]: value,
					},
				}));
				return;
			}

			// Extract processor-specific key from editingCellId
			// Format: `${lineItemId}_${processor}_${columnKey}`
			const parts = editingCellId.split('_');
			if (parts.length >= 2) {
				const processorKey = parts[1]; // 'ic' or 'icplus'
				const processorSpecificKey = `${lineItemId}_${processorKey}`;
				setLineItemEdits((prev) => ({
					...prev,
					[processorSpecificKey]: {
						...prev[processorSpecificKey],
						[field]: value,
					},
				}));
			} else {
				// Fallback to original behavior
				setLineItemEdits((prev) => ({
					...prev,
					[lineItemId]: {
						...prev[lineItemId],
						[field]: value,
					},
				}));
			}
		},
		[editingCellId],
	);

	const handleSaveEdit = useCallback(async () => {
		if (!editingCellId) return;

		// Extract lineItemId, processor, and columnKey from cellId
		// Format: `${lineItemId}_${processor}_${columnKey}`
		const parts = editingCellId.split('_');
		if (parts.length < 3) return;

		const actualLineItemId = parts[0];
		const processor = parts[1]; // 'ic' or 'icplus'
		const columnKey = parts.slice(2).join('_'); // Handle column keys with underscores

		// CRITICAL: Use processor-specific key to get the correct edit state
		const processorSpecificKey = `${actualLineItemId}_${processor}`;
		const edit = lineItemEdits[processorSpecificKey];
		if (!edit) return;

		// Block saving an empty / non-numeric value for numeric columns. The
		// field can be cleared while editing, but an empty value must NOT be
		// persisted (it would silently save as 0). Keep the cell in edit mode so
		// the user has to enter a value before it can be saved.
		const numericFieldByColumn: Record<string, keyof LineItemEditState> = {
			rate: 'rate',
			fee: 'per_transaction_fee',
			volume: 'volume',
			count: 'transaction_count',
		};
		const numericField = numericFieldByColumn[columnKey];
		if (numericField) {
			const rawValue = edit[numericField];
			if (rawValue !== undefined) {
				const isEmpty =
					rawValue === null ||
					(typeof rawValue === 'string' && rawValue.trim() === '');
				const parsedValue =
					typeof rawValue === 'string'
						? parseFloat(rawValue.replace(/[$,%]/g, ''))
						: rawValue;
				if (isEmpty || Number.isNaN(parsedValue)) {
					return;
				}
			}
		}

		// Find the original item to get current values
		// We need to find it from the data - this is a bit tricky, we'll need to pass it differently
		// For now, we'll use the edit state which has the original values

		const parseRate = (rateStr: string): number => {
			if (!rateStr || rateStr === '-') return 0;
			const num = parseFloat(rateStr.replace('%', '')) / 100;
			return isNaN(num) ? 0 : num;
		};

		const parseCurrency = (currencyStr: string): number => {
			if (!currencyStr || currencyStr === '-') return 0;
			const num = parseFloat(currencyStr.replace(/[$,]/g, ''));
			return isNaN(num) ? 0 : num;
		};

		// Determine which field was edited based on columnKey
		let fieldChanged = false;
		const updates: {
			description?: string;
			rate?: number;
			per_transaction_fee?: number;
			volume?: number;
			transaction_count?: number;
		} = {};

		if (columnKey === 'description' && edit.description !== undefined) {
			updates.description = edit.description;
			fieldChanged = true;
		} else if (columnKey === 'rate' && edit.rate !== undefined) {
			// Rate is stored as string (percentage format) during editing, convert to decimal
			const rateValue =
				typeof edit.rate === 'string'
					? parseFloat((edit.rate as string).replace('%', '')) / 100
					: edit.rate;
			if (!isNaN(rateValue)) {
				updates.rate = rateValue;
				fieldChanged = true;
			}
		} else if (columnKey === 'fee' && edit.per_transaction_fee !== undefined) {
			const feeValue = typeof edit.per_transaction_fee === 'string'
				? parseFloat(edit.per_transaction_fee)
				: edit.per_transaction_fee;
			updates.per_transaction_fee = isNaN(feeValue) ? 0 : feeValue;
			fieldChanged = true;
		} else if (columnKey === 'volume' && edit.volume !== undefined) {
			const volValue = typeof edit.volume === 'string'
				? parseFloat(edit.volume)
				: edit.volume;
			updates.volume = isNaN(volValue) ? 0 : volValue;
			fieldChanged = true;
		} else if (columnKey === 'count' && edit.transaction_count !== undefined) {
			updates.transaction_count = edit.transaction_count;
			fieldChanged = true;
		}

		if (!fieldChanged) {
			setEditingCellId(null);
			return;
		}

		setSavingLineItemId(actualLineItemId);

		try {
			const result = await updateLineItem({
				lineItemId: actualLineItemId,
				fileId, // Pass fileId so we can invalidate the specific file's LineItems tag
				updates: {
					...updates,
					processor_type: processor, // Pass 'ic' or 'icplus' to indicate which processor is being edited
				},
			}).unwrap();

			// Clear edit state on success
			setEditingCellId(null);
			setLineItemEdits((prev) => {
				const newEdits = { ...prev };
				delete newEdits[actualLineItemId];
				return newEdits;
			});

			// Switch to user_edits view mode so the user can see their changes
			if (onViewModeChange) {
				onViewModeChange('user_edits');
			}

			// Notify parent to refresh line items ONLY (not detailed API)
			// This will refetch getLineItems query, which is the single source of truth
			setIsRefetchingAfterSave(true);
			if (onLineItemUpdated) {
				await onLineItemUpdated();
			}
			// Wait a bit for React to re-render with fresh data
			setTimeout(() => {
				setIsRefetchingAfterSave(false);
			}, 100);
		} catch (error) {
			console.error('Error updating line item:', error);
		} finally {
			setSavingLineItemId(null);
		}
	}, [
		editingCellId,
		lineItemEdits,
		updateLineItem,
		onLineItemUpdated,
		onViewModeChange,
	]);

	const hasChanges = useCallback(
		(
			lineItemId: string,
			originalItem: UnifiedFeeItem | UnifiedOtherService,
			isCurrentProcessor: boolean,
		): boolean => {
			const edit = lineItemEdits[lineItemId];
			if (!edit) return false;

			const isOtherService = !('rate' in (originalItem as any).ic);
			const originalDataSource = isCurrentProcessor
				? isOtherService
					? (originalItem as UnifiedOtherService).ic
					: (originalItem as UnifiedFeeItem).ic
				: isOtherService
					? (originalItem as UnifiedOtherService).icPlus
					: (originalItem as UnifiedFeeItem).icPlus;

			const parseRate = (rateStr: string): number => {
				if (!rateStr || rateStr === '-') return 0;
				const num = parseFloat(rateStr.replace('%', '')) / 100;
				return isNaN(num) ? 0 : num;
			};

			const parseCurrency = (currencyStr: string): number => {
				if (!currencyStr || currencyStr === '-') return 0;
				const num = parseFloat(currencyStr.replace(/[$,]/g, ''));
				return isNaN(num) ? 0 : num;
			};

			const originalRate = isOtherService
				? undefined
				: parseRate((originalDataSource as any).rate || '0%');
			const originalPerTrans =
				(originalDataSource as any).perTrans ??
				(originalDataSource as any).fee ??
				0;
			const originalVolume = isOtherService
				? undefined
				: parseCurrency((originalDataSource as any).volume?.toString() || '0');
			const originalAmount = originalDataSource.feeDue;
			const originalDescription = originalItem.type;

			const descriptionChanged = edit.description !== originalDescription;
			const amountChanged =
				edit.amount !== undefined &&
				Math.abs(edit.amount - originalAmount) > 0.01;
			const rateChanged =
				edit.rate !== undefined &&
				originalRate !== undefined &&
				Math.abs(edit.rate - originalRate) > 0.0001;
			const perTransChanged =
				edit.per_transaction_fee !== undefined &&
				Math.abs(edit.per_transaction_fee - originalPerTrans) > 0.01;
			const volumeChanged =
				edit.volume !== undefined &&
				originalVolume !== undefined &&
				Math.abs(edit.volume - originalVolume) > 0.01;

			return (
				descriptionChanged ||
				amountChanged ||
				rateChanged ||
				perTransChanged ||
				volumeChanged
			);
		},
		[lineItemEdits],
	);

	const getCurrentValue = useCallback(
		(
			lineItemId: string,
			field: keyof LineItemEditState,
			originalValue: string | number | undefined,
			isCurrentProcessor?: boolean,
		): string | number | undefined => {
			// CRITICAL: Use processor-specific key to prevent edits from one side appearing on the other
			// If isCurrentProcessor is provided, use it to determine the processor-specific key
			if (isCurrentProcessor !== undefined) {
				const processorKey = isCurrentProcessor ? 'ic' : 'icplus';
				const processorSpecificKey = `${lineItemId}_${processorKey}`;
				const edit = lineItemEdits[processorSpecificKey];
				if (edit && edit[field] !== undefined) {
					return edit[field] as string | number;
				}
			}

			// Fallback: If editingCellId is set, use it to determine which processor we're editing
			if (editingCellId) {
				const parts = editingCellId.split('_');
				if (parts.length >= 2) {
					const processorKey = parts[1]; // 'ic' or 'icplus'
					const processorSpecificKey = `${lineItemId}_${processorKey}`;
					const edit = lineItemEdits[processorSpecificKey];
					if (edit && edit[field] !== undefined) {
						return edit[field] as string | number;
					}
				}
			}

			// Final fallback: check original key (for backward compatibility)
			const edit = lineItemEdits[lineItemId];
			if (edit && edit[field] !== undefined) {
				return edit[field] as string | number;
			}

			return originalValue;
		},
		[lineItemEdits, editingCellId],
	);

	// Check if a specific field has been changed for a line item
	const hasFieldChanged = useCallback(
		(
			lineItemId: string,
			field: 'rate' | 'fee' | 'volume' | 'count' | 'feeDue',
			originalItem: UnifiedFeeItem | UnifiedOtherService,
			isCurrentProcessor: boolean,
		): boolean => {
			// CRITICAL: Use processor-specific key to check edits for the correct processor side
			const processorKey = isCurrentProcessor ? 'ic' : 'icplus';
			const processorSpecificKey = `${lineItemId}_${processorKey}`;
			let edit = lineItemEdits[processorSpecificKey];
			if (!edit) {
				// Fallback: check original key (for backward compatibility)
				edit = lineItemEdits[lineItemId];
				if (!edit) return false;
			}

			const isOtherService = !('rate' in (originalItem as any).ic);
			const originalDataSource = isCurrentProcessor
				? isOtherService
					? (originalItem as UnifiedOtherService).ic
					: (originalItem as UnifiedFeeItem).ic
				: isOtherService
					? (originalItem as UnifiedOtherService).icPlus
					: (originalItem as UnifiedFeeItem).icPlus;

			const parseRate = (rateStr: string): number => {
				if (!rateStr || rateStr === '-') return 0;
				const num = parseFloat(rateStr.replace('%', '')) / 100;
				return isNaN(num) ? 0 : num;
			};

			const parseCurrency = (currencyStr: string): number => {
				if (!currencyStr || currencyStr === '-') return 0;
				const num = parseFloat(currencyStr.replace(/[$,]/g, ''));
				return isNaN(num) ? 0 : num;
			};

			switch (field) {
				case 'rate': {
					if (isOtherService) return false;
					const originalRate = parseRate(
						(originalDataSource as any).rate || '0%',
					);
					return (
						edit.rate !== undefined &&
						Math.abs(edit.rate - originalRate) > 0.0001
					);
				}
				case 'fee': {
					const originalPerTrans =
						(originalDataSource as any).perTrans ??
						(originalDataSource as any).fee ??
						0;
					return (
						edit.per_transaction_fee !== undefined &&
						Math.abs(edit.per_transaction_fee - originalPerTrans) > 0.01
					);
				}
				case 'volume': {
					if (isOtherService) return false;
					const originalVolume = parseCurrency(
						(originalDataSource as any).volume?.toString() || '0',
					);
					return (
						edit.volume !== undefined &&
						Math.abs(edit.volume - originalVolume) > 0.01
					);
				}
				case 'count': {
					const originalCount = (originalDataSource as any).count || 0;
					return (
						edit.transaction_count !== undefined &&
						edit.transaction_count !== originalCount
					);
				}
				case 'feeDue': {
					const originalAmount = originalDataSource.feeDue;
					return (
						edit.amount !== undefined &&
						Math.abs(edit.amount - originalAmount) > 0.01
					);
				}
				default:
					return false;
			}
		},
		[lineItemEdits],
	);

	// Audit log modal handlers
	const handleOpenAuditLog = useCallback(
		(lineItemId: string, lineItemDescription: string) => {
			setAuditLogModal({
				isOpen: true,
				lineItemId,
				lineItemDescription,
			});
		},
		[],
	);

	const handleCloseAuditLog = useCallback(() => {
		setAuditLogModal({
			isOpen: false,
			lineItemId: '',
			lineItemDescription: '',
		});
	}, []);

	// Feedback API hooks
	const [submitFeedback] = useSubmitFeedbackMutation();

	// Initialize local feedback state from data prop
	useEffect(() => {
		const feedbackMap: Record<string, any> = {};
		data.forEach((item) => {
			if (item.feedback) {
				feedbackMap[item.line_item_id] = item.feedback;
			}
		});
		// Merge with existing local feedback to preserve any updates
		setLocalFeedback((prev) => ({
			...prev,
			...feedbackMap,
		}));
	}, [data]);

	const handleFeedbackClick = useCallback(
		async (ratingType: 'THUMBS_UP' | 'THUMBS_DOWN', lineItemId: string) => {
			try {
				// First API call - submit initial feedback
				await submitFeedback({
					file_id: fileId,
					line_item_id: lineItemId,
					rating_type: ratingType,
				}).unwrap();

				// Update local feedback state with the submitted feedback
				const newFeedback = {
					id: Date.now().toString(), // Temporary ID
					file_id: fileId,
					line_item_id: lineItemId,
					rating_type: ratingType,
					created_at: new Date().toISOString(),
					updated_at: new Date().toISOString(),
					user_id: '', // Will be set by backend
					enterprise_id: '', // Will be set by backend
				};

				setLocalFeedback((prev) => ({
					...prev,
					[lineItemId]: newFeedback,
				}));

				// Show comment modal for both thumbs up and thumbs down
				setFeedbackModal({
					isOpen: true,
					ratingType,
					lineItemId,
					showCommentModal: true,
				});
			} catch (error) {
				console.error('Error submitting feedback:', error);
			}
		},
		[submitFeedback, fileId],
	);

	const handleFeedbackSubmit = useCallback(
		async (
			ratingType: 'THUMBS_UP' | 'THUMBS_DOWN',
			reasonCode?: string,
			note?: string,
		) => {
			try {
				// Second API call - update with additional details
				const payload: any = {
					file_id: fileId,
					line_item_id: feedbackModal.lineItemId,
					rating_type: ratingType,
					note: note || '',
				};

				if (ratingType === 'THUMBS_DOWN' && reasonCode) {
					payload.reason_code = reasonCode;
				}

				await submitFeedback(payload).unwrap();

				setLocalFeedback((prev) => ({
					...prev,
					[feedbackModal.lineItemId]: {
						...prev[feedbackModal.lineItemId],
						...(ratingType === 'THUMBS_DOWN' &&
							reasonCode && { reason_code: reasonCode }),
						note: note || '',
						updated_at: new Date().toISOString(),
					},
				}));

				setFeedbackModal((prev) => ({ ...prev, isOpen: false }));
			} catch (error) {
				console.error('Error updating feedback:', error);
			}
		},
		[submitFeedback, fileId, feedbackModal.lineItemId],
	);

	const getColumnCount = () => {
		let count = 2; // # and Rate are always present
		if (columns.includes('fee')) count += 1;
		if (columns.includes('perTrans')) count += 1;
		count += 2; // Volume and Fee Due are always present
		return count;
	};

	const columnCount = getColumnCount();

	const { totalCurrent, totalWinstar, isNegativeSavings } = useMemo(() => {
		const calculateFeeDue = (
			items: (UnifiedFeeItem | UnifiedOtherService)[],
			processor: 'ic' | 'icPlus',
		): number => {
			return items.reduce((sum, item) => {
				const isOtherService = !('rate' in (item as any).ic);
				const feeDue = isOtherService
					? (item as UnifiedOtherService)[processor].feeDue
					: (item as UnifiedFeeItem)[processor].feeDue;

				const itemTotal =
					isNaN(feeDue) || feeDue === null || feeDue === undefined ? 0 : feeDue;
				return sum + itemTotal;
			}, 0);
		};

		const current = calculateFeeDue(data, 'ic');
		const winstar = calculateFeeDue(data, 'icPlus');
		const savingsAmount = current - winstar;
		const negativeSavings = isNaN(savingsAmount) ? false : savingsAmount < 0;

		return {
			totalCurrent: isNaN(current) ? 0 : current,
			totalWinstar: isNaN(winstar) ? 0 : winstar,
			savings: isNaN(savingsAmount) ? 0 : savingsAmount,
			isNegativeSavings: negativeSavings,
		};
	}, [data]);

	const tableColumns = [
		{ key: 'count', label: '#', width: 'w-16', align: 'text-left' },
		{ key: 'rate', label: 'Rate', width: 'flex-1', align: 'text-left' },
		{ key: 'fee', label: 'Fee', width: 'flex-1', align: 'text-left' },
		{ key: 'volume', label: 'Volume', width: 'flex-1', align: 'text-left' },
		{ key: 'feeDue', label: 'Fee due', width: 'flex-1', align: 'text-right' },
	];

	return (
		<div
			className="bg-white flex flex-col justify-start items-start"
			role="table"
			aria-label={`${title} analysis table`}
		>
			<div className="w-full px-7 py-4 flex flex-col justify-start items-start">
				<div className="flex items-center gap-2">
					<div className="text-black text-base font-semibold leading-tight font-primary">
						{title}
					</div>
					<div className="group relative inline-block">
						<Info className="w-4 h-4 text-gray-400 cursor-help hover:text-gray-600 transition-colors" />
						<div className="invisible group-hover:visible absolute left-0 top-6 z-50 w-80 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
							<div className="font-semibold mb-2">Winner Indicators:</div>
							<div className="space-y-1 mb-3">
								<div className="flex items-center gap-2">
									<span className="text-green-500 font-semibold">$123.45</span>
									<span>Lower fee (better rate)</span>
								</div>
							</div>
							<div className="font-semibold mb-2">
								Padded Rate Detection (Current Processor):
							</div>
							<div className="space-y-1">
								<div className="flex items-center gap-2">
									<div className="w-4 h-4 bg-sand-100 border border-sand-300 rounded"></div>
									<span>Minor overcharge (5-10%)</span>
								</div>
								<div className="flex items-center gap-2">
									<div className="w-4 h-4 bg-sand-200 border border-sand-400 rounded"></div>
									<span>Moderate overcharge (10-20%)</span>
								</div>
								<div className="flex items-center gap-2">
									<div className="w-4 h-4 bg-red-200 border border-red-300 rounded"></div>
									<span>High overcharge (20-30%)</span>
								</div>
								<div className="flex items-center gap-2">
									<div className="w-4 h-4 bg-red-300 border border-red-400 rounded"></div>
									<span>Critical overcharge (&gt;30%)</span>
								</div>
								<div className="text-gray-400 text-xxs mt-2">
									Hover over highlighted cells for details
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div
				className="w-full inline-flex justify-start items-start overflow-visible"
				role="rowgroup"
			>
				<div className="flex-1 bg-white flex justify-start items-start overflow-visible">
					{/* Fee Names Column */}
					<div
						className="w-[400px] flex-shrink-0 inline-flex flex-col justify-start items-start"
						role="column"
					>
						<div
							className="w-full h-10 pl-7 pr-3 bg-black/5 border-b border-black/5 flex flex-col justify-center items-start"
							role="columnheader"
						>
							<div className="text-black text-xs font-semibold leading-none font-primary">
								Processing month: {processingMonth}
							</div>
						</div>
						<div
							className="w-full h-10 pl-7 pr-3 bg-white border-b border-black/4 flex flex-col justify-center items-start"
							role="columnheader"
						>
							<div className="text-black text-xs font-semibold font-primary leading-none">
								{' '}
							</div>
						</div>
						{showLoader ? (
							<div className="w-full h-64 flex items-center justify-center">
								<Loader text="Loading data..." size="lg" />
							</div>
						) : (
							<DroppableCategory
								id={`category_${sectionKey || title.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`}
								isDragging={activeId !== null}
								isSource={
									draggedFromCategory ===
									`category_${sectionKey || title.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`
								}
								draggedItemHasChildren={externalDraggedItemHasChildren}
							>
								{/* Gap drop zone at the top - before first item */}
								{enableEditing && sortedData.length > 0 && (
									<GapDropZone
										id={`${sectionKey || title.toLowerCase().replace(/[^a-z0-9]+/g, '_')}-top-gap`}
										categoryId={`category_${sectionKey || title.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`}
										isDragging={externalActiveId !== null}
										disabled={externalDraggedItemHasChildren}
										itemBelow={sortedData[0]} // Pass the first item for display_order calculation
									/>
								)}
								{sortedData.map((item, index) => {
									// Use unique identifier: item.id (database UUID) for root items, or line_item_id with index for uniqueness
									// Fallback to line_item_id_ic_plus if line_item_id is empty
									// This ensures each row has a unique hover identifier, even if line_item_id is duplicated
									const uniqueRowId =
										item.id ||
										item.line_item_id ||
										item.line_item_id_ic_plus ||
										`item_${index}`;
									const lineItemId =
										item.line_item_id || item.line_item_id_ic_plus || '';
									const isHovered =
										hoveredRow === uniqueRowId || hoveredRow === lineItemId;
									const currentFeedback =
										localFeedback[lineItemId]?.rating_type ||
										item.feedback?.rating_type;
									const shouldShowButtons =
										(isHovered || Boolean(currentFeedback)) && lineItemId;
									const hasChildren =
										showChildren && item.children && item.children.length > 0;
									const isExpanded = expandedRows.has(lineItemId);
									// Description editing is row-wide, but we need to track which processor's line item
									// For description, we'll use the IC line item ID (description is shared)
									// Description is not editable - removed editing state
									const isSaving =
										savingLineItemId === lineItemId ||
										savingLineItemId === item.line_item_id_ic_plus;
									const editedDescription = getCurrentValue(
										lineItemId,
										'description',
										item.type,
									) as string;
									const itemHasChanges = hasChanges(lineItemId, item, true); // true = isCurrentProcessor
									// Check if item has been moved (persistent undo markers) OR has history
									// Check if item has move_history in extra_attributes (backend-persisted move history)
									// move_history is an array of move operations - if it exists and has entries, item can be undone
									const moveHistory = item.extra_attributes?.move_history;
									const hasMoveHistory =
										Array.isArray(moveHistory) && moveHistory.length > 0;

									// Check if item can be undone:
									// 1. Item is in dropHistory (session-based undo for multi-step drags) - legacy support
									//    - Check line_item_id (IC item ID)
									//    - Check item.id as fallback (for items that might use id instead)
									//    - Check line_item_id_ic_plus (IC+ item ID)
									// 2. Item has move_history in backend (new persistent undo system)
									const canUndoFromHistory =
										(lineItemId && undoableItemIds.has(lineItemId)) ||
										(item.id && undoableItemIds.has(item.id)) ||
										(item.line_item_id_ic_plus &&
											undoableItemIds.has(item.line_item_id_ic_plus));

									// Item can be undone if it has move_history (backend-persisted)
									// History only contains active moves, so if it exists and has entries, we can undo
									const canUndoFromPersistent = hasMoveHistory;

									// Item can be undone if it's in drop history (legacy) OR has move_history (new system)
									const canUndoItem =
										canUndoFromHistory || canUndoFromPersistent;

									return (
										<React.Fragment key={index}>
											{/* Wrap row in relative container for absolute positioning of GapDropZone */}
											<div className="relative">
												<DraggableRow
													id={uniqueRowId}
													disabled={!enableEditing}
													item={item}
													categoryId={`category_${title.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`}
													isParent={true}
													draggedItemHasChildren={
														externalDraggedItemHasChildren
													}
													isDraggingActive={externalActiveId !== null}
													isRecentlyDropped={recentlyDroppedIds.has(lineItemId)}
													canUndo={canUndoItem}
													onUndo={onUndoDrop}
													isUndoing={isUndoing}
												>
													<div
														className={`relative w-full h-10 min-w-10 border-b border-gray-100 flex items-center justify-between gap-2 transition-colors ${getRowBackgroundColor(isHovered, hasMoveHistory, item, sortedData)} ${flaggedLines?.has(lineItemId) ? FLAGGED_ROW_CLASS : ''}`}
														role="row"
														aria-label={`Row ${index + 1}: ${item.type}`}
														onMouseEnter={() => setHoveredRow(uniqueRowId)}
														onMouseLeave={() => setHoveredRow(null)}
													>
														{/* Expand/Collapse button */}
														{hasChildren ? (
															<button
																onClick={() => onToggleRow(lineItemId)}
																className="flex-shrink-0 p-0.5 hover:bg-gray-100 rounded"
																aria-label={isExpanded ? 'Collapse' : 'Expand'}
															>
																{isExpanded ? (
																	<ChevronDown className="w-4 h-4 text-gray-600" />
																) : (
																	<ChevronRight className="w-4 h-4 text-gray-600" />
																)}
															</button>
														) : (
															<div className="flex-shrink-0 w-6" />
														)}
														{/* Description - Not editable */}
														<span className="flex-1 min-w-0 text-black text-sm font-normal leading-tight flex items-center gap-1.5">
															<span className="truncate">{item.type}</span>
															<LineReviewBadge lineItemId={lineItemId} />
															{item.is_estimated && (
																<span className="flex-shrink-0 inline-flex items-center px-1 py-0.5 rounded text-[9px] font-medium bg-amber-50 text-amber-600 border border-amber-200 cursor-help group relative">
																	Est.
																	<span className="absolute hidden group-hover:block z-50 w-48 p-2 -top-1 left-full ml-1 bg-gray-900 text-white text-xs font-normal rounded shadow-lg whitespace-normal pointer-events-none">
																		Estimated using industry average rates.
																		Actual fees may vary.
																	</span>
																</span>
															)}
														</span>
														{/* Feedback buttons - Right aligned */}
														<div
															className="flex items-center space-x-1 flex-shrink-0 pr-3"
															role="group"
															aria-label="Feedback options"
														>
															{/* Thumbs up/down buttons - show on hover or when feedback exists */}
															{shouldShowButtons && (
																<>
																	<button
																		onClick={() =>
																			handleFeedbackClick(
																				'THUMBS_UP',
																				lineItemId,
																			)
																		}
																		className={`p-1 rounded-full transition-colors ${
																			currentFeedback === 'THUMBS_UP'
																				? 'text-[#4739C7] bg-purple-100'
																				: 'text-gray-400'
																		}`}
																		title="Thumbs up"
																		aria-label={`Rate ${item.type} as positive`}
																	>
																		<ThumbsUpIcon
																			weight={
																				currentFeedback === 'THUMBS_UP'
																					? 'fill'
																					: 'regular'
																			}
																			className="w-4 h-4"
																		/>
																	</button>
																	<button
																		onClick={() =>
																			handleFeedbackClick(
																				'THUMBS_DOWN',
																				lineItemId,
																			)
																		}
																		className={`p-1 rounded-full transition-colors ${
																			currentFeedback === 'THUMBS_DOWN'
																				? 'text-[#4739C7] bg-purple-100'
																				: 'text-gray-400'
																		}`}
																		title="Thumbs down"
																		aria-label={`Rate ${item.type} as negative`}
																	>
																		<ThumbsDownIcon
																			weight={
																				currentFeedback === 'THUMBS_DOWN'
																					? 'fill'
																					: 'regular'
																			}
																			className="w-4 h-4"
																		/>
																	</button>
																</>
															)}
															{/* Audit Log button - always show if item has history (has changes or has been moved) */}
															{enableEditing &&
																(itemHasChanges ||
																	hasMoveHistory ||
																	item.modified_fields_ic ||
																	item.modified_fields_ic_plus) && (
																	<button
																		onClick={() =>
																			handleOpenAuditLog(lineItemId, item.type)
																		}
																		className="p-1 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
																		title="View edit history"
																		aria-label={`View edit history for ${item.type}`}
																	>
																		<History className="w-4 h-4" />
																	</button>
																)}
															{/* Undo button - shows when item can be undone (last, on the right) */}
															{enableEditing && canUndoItem && onUndoDrop && (
																<button
																	onClick={(e) => {
																		e.stopPropagation();
																		onUndoDrop(item);
																	}}
																	disabled={isUndoing}
																	className="p-1 rounded text-amber-600 hover:text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
																	title="Undo move"
																	aria-label="Undo last move"
																>
																	<Undo2 className="w-4 h-4" />
																</button>
															)}
														</div>
													</div>
												</DraggableRow>
												{/* Gap drop zone between items - positioned absolutely so it doesn't affect row height */}
												{/* Container with zero height so GapDropZone doesn't affect layout */}
												{enableEditing && index < sortedData.length - 1 && (
													<div className="relative h-0 w-full">
														<GapDropZone
															id={`${uniqueRowId}-gap`}
															categoryId={`category_${title.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`}
															isDragging={externalActiveId !== null}
															disabled={externalDraggedItemHasChildren}
															itemAbove={item}
															itemBelow={sortedData[index + 1]}
														/>
													</div>
												)}
											</div>
											{/* Render children if expanded */}
											{hasChildren &&
												isExpanded &&
												item.children!.map((child, childIndex) => {
													const childLineItemId = child.line_item_id;
													const childIsHovered = hoveredRow === childLineItemId;
													const childFeedback =
														localFeedback[childLineItemId]?.rating_type ||
														child.feedback?.rating_type;
													const childShouldShowButtons =
														(childIsHovered || Boolean(childFeedback)) &&
														childLineItemId;
													const isLastChild =
														childIndex === item.children!.length - 1;
													// Child description is not editable - removed editing state
													const childIsSaving =
														savingLineItemId === childLineItemId ||
														savingLineItemId === child.line_item_id_ic_plus;
													// Check if child has move_history in extra_attributes (backend-persisted move history)
													const childMoveHistory =
														child.extra_attributes?.move_history;
													const childHasMoveHistory =
														Array.isArray(childMoveHistory) &&
														childMoveHistory.length > 0;

													// Child can be undone if:
													// 1. It's in drop history (legacy session-based), OR
													// 2. It has move_history (backend-persisted)
													const childCanUndoFromHistory =
														(childLineItemId &&
															undoableItemIds.has(childLineItemId)) ||
														(child.id && undoableItemIds.has(child.id)) ||
														(child.line_item_id_ic_plus &&
															undoableItemIds.has(child.line_item_id_ic_plus));
													const childCanUndoFromPersistent =
														childHasMoveHistory;
													const childCanUndo =
														childCanUndoFromHistory ||
														childCanUndoFromPersistent;

													return (
														<DraggableRow
															key={childLineItemId}
															id={childLineItemId}
															disabled={!enableEditing}
															item={child}
															categoryId={`category_${title.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`}
															isParent={false}
															draggedItemHasChildren={
																externalDraggedItemHasChildren
															}
															isDraggingActive={externalActiveId !== null}
															isRecentlyDropped={recentlyDroppedIds.has(
																childLineItemId,
															)}
															canUndo={childCanUndo}
															onUndo={onUndoDrop}
															isUndoing={isUndoing}
														>
															<div
																className={`relative w-full h-10 min-w-10 border-b flex items-center gap-2 transition-colors ${
																	getRowBackgroundColor(
																		childIsHovered,
																		childHasMoveHistory,
																		child,
																		item.children || [],
																		isCurrentProcessor,
																	) || 'bg-white hover:bg-gray-50/50'
																} ${isLastChild ? 'border-gray-200' : 'border-gray-100'} ${flaggedLines?.has(childLineItemId) ? FLAGGED_ROW_CLASS : ''}`}
																role="row"
																aria-label={`Child row ${childIndex + 1}: ${child.type}`}
																onMouseEnter={() =>
																	setHoveredRow(childLineItemId)
																}
																onMouseLeave={() => setHoveredRow(null)}
															>
																<span className="text-gray-400 text-xs">
																	└─
																</span>
																{/* Child Description - Not editable */}
																<span className="flex-1 min-w-0 text-gray-700 text-xs font-medium leading-tight uppercase tracking-wide flex items-center gap-1">
																	<span className="truncate">{child.type}</span>
																	<LineReviewBadge lineItemId={childLineItemId} />
																	{child.is_estimated && (
																		<span className="flex-shrink-0 inline-flex items-center px-1 py-0.5 rounded text-[8px] font-medium bg-amber-50 text-amber-600 border border-amber-200 cursor-help group relative normal-case">
																			Est.
																			<span className="absolute hidden group-hover:block z-50 w-44 p-2 -top-1 left-full ml-1 bg-gray-900 text-white text-xs font-normal rounded shadow-lg whitespace-normal pointer-events-none">
																				Estimated using industry averages.
																				Actual fees may vary.
																			</span>
																		</span>
																	)}
																</span>
																{/* Feedback buttons for children - Right aligned */}
																<div
																	className="flex items-center space-x-1 flex-shrink-0 pr-3"
																	role="group"
																	aria-label="Feedback options"
																>
																	{/* Thumbs up/down buttons - show on hover or when feedback exists */}
																	{childShouldShowButtons && (
																		<>
																			<button
																				onClick={() =>
																					handleFeedbackClick(
																						'THUMBS_UP',
																						childLineItemId,
																					)
																				}
																				className={`p-1 rounded-full transition-colors ${
																					childFeedback === 'THUMBS_UP'
																						? 'text-[#4739C7] bg-purple-100'
																						: 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
																				}`}
																				title="Thumbs up"
																				aria-label={`Rate ${child.type} as positive`}
																			>
																				<ThumbsUpIcon
																					weight={
																						childFeedback === 'THUMBS_UP'
																							? 'fill'
																							: 'regular'
																					}
																					className="w-4 h-4"
																				/>
																			</button>
																			<button
																				onClick={() =>
																					handleFeedbackClick(
																						'THUMBS_DOWN',
																						childLineItemId,
																					)
																				}
																				className={`p-1 rounded-full transition-colors ${
																					childFeedback === 'THUMBS_DOWN'
																						? 'text-[#4739C7] bg-purple-100'
																						: 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
																				}`}
																				title="Thumbs down"
																				aria-label={`Rate ${child.type} as negative`}
																			>
																				<ThumbsDownIcon
																					weight={
																						childFeedback === 'THUMBS_DOWN'
																							? 'fill'
																							: 'regular'
																					}
																					className="w-4 h-4"
																				/>
																			</button>
																		</>
																	)}
																	{/* Audit Log button for children - always show if child has history */}
																	{enableEditing &&
																		(childHasMoveHistory ||
																			child.modified_fields_ic ||
																			child.modified_fields_ic_plus) && (
																			<button
																				onClick={() =>
																					handleOpenAuditLog(
																						childLineItemId,
																						child.type,
																					)
																				}
																				className="p-1 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
																				title="View edit history"
																				aria-label={`View edit history for ${child.type}`}
																			>
																				<History className="w-4 h-4" />
																			</button>
																		)}
																	{/* Undo button for children - shows when child can be undone (was moved here from being a root item) */}
																	{enableEditing &&
																		childCanUndo &&
																		onUndoDrop && (
																			<button
																				onClick={(e) => {
																					e.stopPropagation();
																					onUndoDrop(child);
																				}}
																				disabled={isUndoing}
																				className="p-1 rounded text-amber-600 hover:text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
																				title="Undo move"
																				aria-label="Undo last move"
																			>
																				<Undo2 className="w-4 h-4" />
																			</button>
																		)}
																</div>
															</div>
														</DraggableRow>
													);
												})}
										</React.Fragment>
									);
								})}
							</DroppableCategory>
						)}
						<div
							className="w-full h-10 pl-7 pr-3 bg-black/5 border-b border-black/5 font-bold flex flex-col justify-center items-start"
							role="row"
							aria-label="Total row"
						>
							<div className="text-black text-xs font-semibold leading-none">
								Total
							</div>
						</div>
					</div>

					<ColumnComponent
						columns={tableColumns}
						data={sortedData}
						isCurrentProcessor={true}
						showLoader={showLoader}
						total={totalCurrent}
						processorName={processorName}
						processorNamePlus={processorNamePlus}
						expandedRows={expandedRows}
						hoveredRow={hoveredRow}
						onHoverChange={setHoveredRow}
						showChildren={showChildren}
						enableEditing={enableEditing}
						editingCellId={editingCellId}
						lineItemEdits={lineItemEdits}
						onEditChange={handleEditChange}
						getCurrentValue={getCurrentValue}
						onStartEdit={handleStartEdit}
						onSaveEdit={handleSaveEdit}
						onCancelEdit={handleCancelEdit}
						savingLineItemId={savingLineItemId}
						sortConfig={sortConfig}
						onSort={handleSort}
						hasFieldChanged={hasFieldChanged}
						showRateDisclaimer={showRateDisclaimer}
					/>

					<ColumnComponent
						columns={tableColumns}
						data={sortedData}
						isCurrentProcessor={false}
						showLoader={showLoader}
						total={totalWinstar}
						processorName={processorName}
						processorNamePlus={processorNamePlus}
						expandedRows={expandedRows}
						hoveredRow={hoveredRow}
						onHoverChange={setHoveredRow}
						showChildren={showChildren}
						enableEditing={enableEditing}
						editingCellId={editingCellId}
						lineItemEdits={lineItemEdits}
						onEditChange={handleEditChange}
						getCurrentValue={getCurrentValue}
						onStartEdit={handleStartEdit}
						onSaveEdit={handleSaveEdit}
						onCancelEdit={handleCancelEdit}
						savingLineItemId={savingLineItemId}
						sortConfig={sortConfig}
						onSort={handleSort}
						hasFieldChanged={hasFieldChanged}
					/>
				</div>
			</div>
			{/* Feedback Modal */}
			{feedbackModal.isOpen && (
				<FeedbackModal
					isOpen={feedbackModal.isOpen}
					onClose={() =>
						setFeedbackModal((prev) => ({ ...prev, isOpen: false }))
					}
					onSubmit={handleFeedbackSubmit}
					ratingType={feedbackModal.ratingType}
					showCommentModal={feedbackModal.showCommentModal}
				/>
			)}
			{/* Audit Log Modal */}
			<LineItemAuditLogModal
				isOpen={auditLogModal.isOpen}
				onClose={handleCloseAuditLog}
				lineItemId={auditLogModal.lineItemId}
				lineItemDescription={auditLogModal.lineItemDescription}
			/>
		</div>
	);
};

export default AnalysisTable;
